import type { Maybe } from '@adornis/base/utilTypes.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { and } from '@adornis/validation/functions/and.js';
import { email } from '@adornis/validation/functions/email.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html } from 'lit';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { GroupsAPI } from '../../groups/api/GroupsAPI.js';
import { Group } from '../../groups/entities/Group.js';
import { School } from '../../groups/entities/School.js';
import { useSelectableSchoolIDs } from '../../permissions/client/useSelectableSchoolIDs.js';
import { Role } from '../../permissions/roles.js';
import { UserAPI } from '../api/UserAPI.js';

interface UserCreationOptions {
  email: string;
  vorname: Maybe<string>;
  nachname: Maybe<string>;
  role: Role;
  schoolId: string;
}

const CreateUserDialog = stateful((props: { resolve: () => void; reject: () => void }) => {
  const [newUser] = useState({
    email: '',
    vorname: null,
    nachname: null,
    role: Role.SUS_BEWERBER,
    schoolId: '',
  } satisfies UserCreationOptions);

  const form = useForm<UserCreationOptions>(newUser, {
    validator: {
      email: and(sgNonOptional(), email()),
      role: sgNonOptional(),
      schoolId: sgNonOptional(),
    },
  });

  // schulleitung and sekretariat may only create users at their own school, so the picker is narrowed down and - if that
  // leaves exactly one school - filled in right away
  const selectableSchoolIDs = useSelectableSchoolIDs();
  const onlySelectableSchoolID = selectableSchoolIDs?.length === 1 ? selectableSchoolIDs[0] : null;
  useEffect(() => {
    if (onlySelectableSchoolID && !form.document.schoolId) form.document.schoolId = onlySelectableSchoolID;
  }, [onlySelectableSchoolID]);

  const handleCreateUser = async () => {
    const errors = await form.validate();
    if (errors) {
      return;
    }

    await guardDialog(
      async () => {
        return await UserAPI.create(
          form.document.email,
          form.document.schoolId,
          form.document.role,
          form.document.vorname ?? '',
          form.document.nachname ?? '',
        )();
      },
      {
        success: personID => {
          props.resolve();
          SgSnackbar.show('Nutzer erfolgreich erstellt');
          goTo(`/personen/${personID}`);
        },
      },
    );
  };

  return html`
    <div class="dialog-container">
      ${DialogHeader({
        title: 'Nutzer erstellen',
        close: props.reject,
        closeLabel: 'Dialog schließen',
      })}

      <div class="flex flex-col gap-2">
        <span class="text-sm font-semibold text-base-content/70">E-Mail-Adresse</span>
        ${TextInput({
          ...form.field('email'),
          placeholder: 'Keine E-Mail-Adresse hinterlegt',
          placeholderMode: 'hide',
        })}
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-semibold text-base-content/70">Vor- und Nachname</span>
        <div class="flex flex-row gap-2">
          ${TextInput({ ...form.field('vorname'), placeholder: 'Kein Vorname hinterlegt', placeholderMode: 'hide' })}
          ${TextInput({ ...form.field('nachname'), placeholder: 'Kein Nachname hinterlegt', placeholderMode: 'hide' })}
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-semibold text-base-content/70">Rolle</span>
        ${DropdownSelectionInput({
          ...form.field('role'),
          placeholder: 'Keine Rolle hinterlegt',
          placeholderMode: 'hide',
          selectables: [Role.SUS_BEWERBER, Role.KANDIDAT],
        })}
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-semibold text-base-content/70">Schule</span>
        ${EntityPickerInput({
          ...form.field('schoolId'),
          placeholder: 'Keine Schule hinterlegt',
          placeholderMode: 'hide',
          class: Group,
          operation: GroupsAPI.subscribeGroups,
          filter: new AdornisFilter({
            searchFields: ['name'],
            aspects: [
              new BooleanAspect({ customID: 'onlyInternal', value: true }),
              new StringAspect({ fieldName: '_class', value: School._class }),
              ...(selectableSchoolIDs ? [new ListAspect({ fieldName: '_id', values: selectableSchoolIDs })] : []),
            ],
          }),

          selectionSet: { name: 1, _id: 1 },
        })}
      </div>

      <button class="btn btn-primary" @click=${handleCreateUser}>Nutzer anlegen</button>
    </div>
  `;
});

export async function openCreateUserDialog() {
  return showDialog({
    content: (resolve, reject) => CreateUserDialog({ resolve, reject }),
    modalBoxClasses: 'max-w-4xl',
  });
}
